// Removed parseMessages import - functionality simplified
import { useAuthStore, useFileStorageStore } from "@/store";
import useThreadStore, {
  INITIAL_THREAD_CONTEXT_STATE,
} from "@/store/threadStore";
import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const useThreadHistory = (thread) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    deleteThread,
    setCurrentThread,
    setCurrentThreadId,
    setCurrentThreadTitle,
    setCurrentThreadContext,
    fetchThreadMessages,
    setIsLoading,
    setMessages,
    threads = [],
    currentThreadId,
    archiveThread,
    unarchiveThread,
  } = useThreadStore();
  const { currentWorkspaceId } = useAuthStore();
  const { getFileByFullPath } = useFileStorageStore();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Helper to handle errors consistently
  const handleError = useCallback((error, fallbackMessage) => {
    console.error(error);
    setError(error);
  }, []);

  const handleThreadSelect = useCallback(
    async (thread) => {
      if (!thread?.id) {
        handleError(new Error("Invalid thread data"), "Invalid thread data");
        return;
      }

      setIsLoading(true);
      try {
        // Set current thread context
        const threadContext = {
          ...INITIAL_THREAD_CONTEXT_STATE,
          id: thread.id,
          title: thread.title || "Untitled Thread",
          openai_thread_id: thread.openai_thread_id,
          workspace_id: thread.workspace_id || currentWorkspaceId,
          created_at: thread.created_at,
          updated_at: thread.updated_at,
          archived: thread.archived || false,
          metadata: thread.metadata || {},
        };

        setCurrentThreadContext(threadContext);
        setCurrentThread(thread);
        setCurrentThreadId(thread.id);
        setCurrentThreadTitle(thread.id, thread.title || "Untitled Thread");

        // Fetch messages for the thread
        const messages = await fetchThreadMessages(thread.id);

        // Process file attachments for each message
        const processedMessages = await Promise.all(
          (messages || []).map(async (message) => {
            if (message.attachments && message.attachments.length > 0) {
              const processedAttachments = await Promise.all(
                message.attachments.map(async (attachment) => {
                  if (attachment.file_id) {
                    try {
                      const file = await getFileByFullPath(attachment.file_id);
                      return {
                        ...attachment,
                        file: file || null,
                      };
                    } catch (error) {
                      console.error(
                        `Error fetching file ${attachment.file_id}:`,
                        error
                      );
                      return attachment;
                    }
                  }
                  return attachment;
                })
              );

              return {
                ...message,
                attachments: processedAttachments,
              };
            }
            return message;
          })
        );

        // Sort messages by created_at timestamp
        const sortedMessages = processedMessages.sort((a, b) => {
          const timeA = new Date(a.created_at).getTime();
          const timeB = new Date(b.created_at).getTime();
          return timeA - timeB;
        });

        // Set the thread messages with a single atomic update
        useThreadStore.setState({
          messages: sortedMessages,
          isLoading: false,
        });

        // Navigate to the thread route if not already there
        if (location.pathname !== `/chat/${thread.id}`) {
          navigate(`/chat/${thread.id}`);
        }
      } catch (error) {
        handleError(error, "Failed to switch conversation");
        // Ensure loading state is reset on error
        useThreadStore.setState({ isLoading: false });
      }
    },
    [
      fetchThreadMessages,
      setCurrentThread,
      setCurrentThreadId,
      setCurrentThreadTitle,
      setCurrentThreadContext,
      setMessages,
      getFileByFullPath,
      setIsLoading,
      handleError,
      navigate,
      location,
      currentWorkspaceId,
    ]
  );

  const handleThreadDelete = useCallback(
    async (thread) => {
      if (!thread?.id) {
        handleError(new Error("Invalid thread data"), "Invalid thread data");
        return;
      }

      setIsProcessing(true);
      try {
        await deleteThread(thread.id);
      } catch (error) {
        handleError(error, "Failed to delete the conversation");
      } finally {
        setIsProcessing(false);
      }
    },
    [deleteThread, handleError]
  );

  const startRenaming = useCallback((thread) => {
    setEditedTitle(thread?.title || "");
    setIsEditing(true);
  }, []);

  const handleTitleChange = useCallback(
    async (thread) => {
      if (!thread?.id) {
        handleError(new Error("Invalid thread data"), "Invalid thread data");
        return;
      }

      setIsProcessing(true);
      try {
        const newTitle = editedTitle.trim();
        if (newTitle && newTitle !== thread?.title) {
          await setCurrentThreadTitle(thread.id, newTitle);
        }
      } catch (error) {
        handleError(error, "Failed to update thread title");
      } finally {
        setIsEditing(false);
        setIsProcessing(false);
      }
    },
    [editedTitle, setCurrentThreadTitle, handleError]
  );

  const handleKeyPress = useCallback(
    (e, thread) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleTitleChange(thread);
      } else if (e.key === "Escape") {
        setIsEditing(false);
        setEditedTitle(thread?.title || "");
      }
    },
    [handleTitleChange]
  );

  const handleThreadArchive = useCallback(
    async (thread) => {
      if (!thread?.id) {
        handleError(new Error("Invalid thread data"), "Invalid thread data");
        return;
      }

      setIsProcessing(true);
      try {
        const isArchived = thread.archived;
        if (isArchived) {
          await unarchiveThread(thread.id);
        } else {
          await archiveThread(thread.id);
        }
      } catch (error) {
        handleError(error, "Failed to archive/unarchive thread");
      } finally {
        setIsProcessing(false);
      }
    },
    [archiveThread, unarchiveThread, handleError]
  );

  const handleThreadShare = useCallback(
    async (thread) => {
      if (!thread?.id) {
        handleError(new Error("Invalid thread data"), "Invalid thread data");
        return;
      }

      setIsProcessing(true);
      try {
        // Generate a shareable link or handle sharing logic
        const shareUrl = `${window.location.origin}/chat/${thread.id}`;
        await navigator.clipboard.writeText(shareUrl);
      } catch (error) {
        handleError(error, "Failed to share thread");
      } finally {
        setIsProcessing(false);
      }
    },
    [handleError]
  );

  return {
    // state
    isEditing,
    editedTitle,
    threads,
    currentThreadId,
    error,
    isProcessing,
    // setters
    setIsEditing,
    setEditedTitle,
    // handlers
    handleThreadSelect,
    handleThreadDelete,
    handleTitleChange,
    handleKeyPress,
    handleThreadArchive,
    handleThreadShare,
    startRenaming,
  };
};

export default useThreadHistory;
